import type { ProductKind } from "@baze/schema";
import type { RenderContext } from "./context";
import { formatRupiah } from "./helpers";
import { Icon, type IconName } from "./icons";
import { cx } from "./ui";

/**
 * A product from the site's Toko as sites see it (the API's public card: never files or links).
 * Comes from the web runtime (or a template's demo seed), like blog posts; not in the definition.
 */
export interface StoreProductCard {
  slug: string;
  kind: ProductKind;
  title: string;
  subtitle?: string;
  description?: string;
  badge?: string;
  cover?: string;
  gallery?: string[];
  /** Tiers ("paket"), cheapest first is not assumed; at least one. */
  tiers: Array<{ id: string; name: string; price: number; compareAt?: number }>;
  highlights?: string[];
  details?: Array<{ label: string; value: string }>;
  /** Public audio sample (beats, sound packs). */
  previewUrl?: string;
  /** Limited stock that is gone. */
  soldOut?: boolean;
  /** What the buyer gets, in words ("2 file unduhan", "1 tautan akses"). */
  includes?: string[];
}

export const KIND_LABELS: Record<ProductKind, string> = { ebook: "E-book", course: "Kelas online", asset: "Aset digital", audio: "Audio", other: "Produk digital" };
export const KIND_ICONS: Record<ProductKind, IconName> = { ebook: "book", course: "play", asset: "sparkle", audio: "music", other: "download" };

/** Cheapest tier: what a card shows ("mulai Rp…" when there are several). */
export function cheapest(p: StoreProductCard) {
  return p.tiers.reduce((a, b) => (b.price < a.price ? b : a), p.tiers[0]!);
}

/** Store products a section shows: by kind, or the owner's picked slugs in their order, capped. */
export function pickProducts(all: readonly StoreProductCard[] | undefined, source: { kinds?: readonly ProductKind[]; slugs?: readonly string[]; limit?: number }): StoreProductCard[] {
  let list = (all ?? []).filter((p) => p.tiers.length > 0);
  if (source.slugs?.length) {
    const bySlug = new Map(list.map((p) => [p.slug, p]));
    list = source.slugs.map((s) => bySlug.get(s)).filter((p): p is StoreProductCard => !!p);
  } else if (source.kinds?.length) {
    list = list.filter((p) => source.kinds!.includes(p.kind));
  }
  return list.slice(0, source.limit ?? 24);
}

export const productHref = (slug: string) => `/produk/${encodeURIComponent(slug)}`;
export const checkoutHref = (slug: string, tier?: string) => `/checkout?p=${encodeURIComponent(slug)}${tier ? `&t=${encodeURIComponent(tier)}` : ""}`;

/** "Rp 79.000" with the strike-through price, and "mulai" when there are several tiers. */
export function StorePrice({ product, className, size = "md" }: { product: StoreProductCard; className?: string; size?: "md" | "lg" }) {
  const t = cheapest(product);
  // A template's sample product has no price yet: nothing rather than "Rp 0".
  if (!t || t.price <= 0) return null;
  return (
    <span className={cx("inline-flex flex-wrap items-baseline gap-x-2", className)}>
      {product.tiers.length > 1 ? <span className="text-xs font-medium text-site-muted">mulai</span> : null}
      <span className={cx("font-semibold tabular-nums", size === "lg" ? "text-3xl @3xl:text-4xl" : "text-lg")}>{formatRupiah(t.price)}</span>
      {t.compareAt && t.compareAt > t.price ? <s className="text-sm text-site-muted tabular-nums">{formatRupiah(t.compareAt)}</s> : null}
    </span>
  );
}

/** More than one product for sale: cards and the product page also offer "Tambah ke keranjang". */
export function showCart(ctx: { products?: readonly StoreProductCard[]; store?: boolean }): boolean {
  return !!ctx.store && (ctx.products?.length ?? 0) > 1;
}

/**
 * "Beli": a plain link to the checkout (works without JavaScript). Inert in the editor.
 * Sold-out products get a disabled label instead.
 */
export function BuyLink({ ctx, product, tier, className, label = "Beli" }: { ctx: RenderContext; product: StoreProductCard; tier?: string; className?: string; label?: string }) {
  const base = cx("baze-btn inline-flex min-h-11 items-center justify-center gap-2 rounded-site-button px-5 text-sm font-semibold transition", className);
  if (product.soldOut) {
    return <span className={cx(base, "cursor-not-allowed bg-site-surface text-site-muted ring-1 ring-site-border")}>Habis</span>;
  }
  return (
    <a
      href={checkoutHref(product.slug, tier)}
      className={cx(base, "bg-site-primary text-site-on-primary shadow-site-sm hover:shadow-site-md")}
      data-baze-buy=""
      data-slug={product.slug}
      {...(tier ? { "data-tier": tier } : {})}
      {...(ctx.editor ? { "data-baze-inert": "" } : {})}
    >
      <Icon name="shopping-bag" className="size-4" />
      {label}
    </a>
  );
}

/**
 * "Tambah ke keranjang", only when the site sells more than one product. `formId` means the tier is the
 * one chosen in that form (the product page); otherwise the button names its tier. runtime/store.ts
 * stores the line and opens the drawer. Inert in the editor.
 */
export function CartAdd({ ctx, product, tier, formId, className }: { ctx: RenderContext; product: StoreProductCard; tier?: string; formId?: string; className?: string }) {
  if (!showCart(ctx) || product.soldOut) return null;
  const chosen = tier ? product.tiers.find((t) => t.id === tier) : formId ? undefined : cheapest(product);
  return (
    <button
      type="button"
      data-baze-cart-add=""
      data-slug={product.slug}
      data-title={product.title}
      {...(chosen ? { "data-tier": chosen.id, "data-tier-name": chosen.name, "data-price": String(chosen.price) } : {})}
      {...(formId ? { "data-form": formId } : {})}
      {...(product.cover ? { "data-cover": product.cover } : {})}
      {...(ctx.editor ? { "data-baze-inert": "" } : {})}
      className={cx(
        "baze-btn inline-flex min-h-11 items-center justify-center gap-2 rounded-site-button border border-site-border bg-site-bg px-4 text-sm font-semibold text-site-text transition hover:border-site-primary",
        className,
      )}
    >
      <Icon name="shopping-bag" className="size-4" />
      Tambah ke keranjang
    </button>
  );
}

/** A small "E-book" / "Kelas online" chip with its icon. */
export function KindChip({ kind, className }: { kind: ProductKind; className?: string }) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full bg-site-surface px-2.5 py-1 text-xs font-semibold text-site-text ring-1 ring-site-border", className)}>
      <Icon name={KIND_ICONS[kind]} className="size-3.5" />
      {KIND_LABELS[kind]}
    </span>
  );
}

/** Play/pause for an audio sample; runtime/store.ts drives one shared <audio> for the whole page. */
export function PlayButton({ ctx, product, className }: { ctx: RenderContext; product: StoreProductCard; className?: string }) {
  if (!product.previewUrl) return null;
  return (
    <button
      type="button"
      data-baze-play={product.previewUrl}
      data-title={product.title}
      aria-label={`Putar contoh ${product.title}`}
      aria-pressed="false"
      disabled={ctx.editor}
      className={cx(
        "group/play relative grid size-11 shrink-0 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-sm transition hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-site-primary disabled:opacity-100",
        className,
      )}
    >
      <Icon name="play" className="size-4 translate-x-px group-aria-pressed/play:hidden" />
      <Icon name="pause" className="hidden size-4 group-aria-pressed/play:block" />
      <svg aria-hidden="true" viewBox="0 0 44 44" className="pointer-events-none absolute inset-0 -rotate-90">
        <circle data-baze-play-ring="" cx="22" cy="22" r="20.5" fill="none" stroke="currentColor" strokeOpacity="0.9" strokeWidth="2" pathLength="100" strokeDasharray="100" strokeDashoffset="100" />
      </svg>
    </button>
  );
}

/** Editor-only stand-in when a store-sourced section has nothing to show yet. */
export function StoreEmpty({ ctx, message }: { ctx: RenderContext; message: string }) {
  if (!ctx.editor) return null;
  return (
    <div className="grid place-items-center gap-2 rounded-site-md border border-dashed border-site-border bg-site-surface px-6 py-12 text-center">
      <Icon name="shopping-bag" className="size-6 text-site-muted" />
      <p className="max-w-sm text-sm text-site-muted">{message}</p>
    </div>
  );
}

/** A selling template's seed products as cards for its demo (priced, no files). */
export function seedCards(seed: { products: Array<{ kind: StoreProductCard["kind"]; title: string; subtitle?: string; description?: string; cover: string; tiers: Array<{ name: string; price: number; compareAt?: number }>; highlights?: string[]; details?: Array<{ label: string; value: string }> }> } | undefined): StoreProductCard[] | undefined {
  if (!seed) return undefined;
  return seed.products.map((p, i) => ({
    slug: `contoh-${i + 1}`,
    kind: p.kind,
    title: p.title,
    subtitle: p.subtitle,
    description: p.description,
    cover: p.cover,
    tiers: p.tiers.map((t, j) => ({ id: `contoh-${i + 1}-${j + 1}`, name: t.name, price: t.price, ...(t.compareAt && t.compareAt > t.price ? { compareAt: t.compareAt } : {}) })),
    highlights: p.highlights,
    details: p.details,
  }));
}
